import {buildStandardHeaders, otsBaseApi} from "../../ots-api";
import {Container, ContainerExportOptions, ContainerPatchDTO} from "../../../model/container";
import {transportCategories} from "../../../model/transport-category";
import {containerLevelApi} from "../container-level/container-level-store";
import {objectChangeLogApi} from "../object-change-log/object-change-log-store";
import {ImportType} from "../../../model/import-type";
import {BaseQueryApi} from "@reduxjs/toolkit/query";
import {OtsAsyncThunkConfigExtra, OtsState} from "../../ots-store";
import {removeUndefined} from "../../../util/util";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Containers", "SiteContainers", "WellContainers", "ContainerClusterContainers"]});

export const containerApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllContainers: builder.query<Container[], string | undefined>({
      query: (regionId) => ({
        url: `/containers/all`,
        method: "GET",
        params: buildParams(regionId)
      }),
      keepUnusedDataFor: 300,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Containers", id} as const)),
            {type: "Containers", id: "LIST"}
          ];
        } else {
          return [{type: "Containers", id: "LIST"}];
        }
      }
    }),
    getContainersBySite: builder.query<
      Container[],
      { regionId: string, siteId: string }
    >({
      query: ({regionId, siteId}) => ({
        url: "/containers/search",
        method: "POST",
        body: {
          regionIds: [regionId],
          siteIds: [siteId]
        }
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Containers", id} as const)
            ),
            {type: "SiteContainers", id: arg.siteId}
          ];
        } else {
          return [];
        }
      }
    }),
    getContainersByWell: builder.query<
      Container[],
      { regionId: string, wellId: string }
    >({
      query: ({regionId, wellId}) => ({
        url: "/containers/search",
        method: "POST",
        body: {
          regionIds: [regionId],
          wellIds: [wellId]
        }
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Containers", id} as const)
            ),
            {type: "WellContainers", id: arg.wellId}
          ];
        } else {
          return [];
        }
      }
    }),
    getContainersByContainerCluster: builder.query<
      Container[],
      { regionId: string, containerClusterId: string }
    >({
      query: ({regionId, containerClusterId}) => ({
        url: "/containers/search",
        method: "POST",
        body: {
          regionIds: [regionId],
          containerClusterIds: [containerClusterId]
        }
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "Containers", id} as const)
            ),
            {type: "ContainerClusterContainers", id: arg.containerClusterId}
          ];
        } else {
          return [];
        }
      }
    }),
    getContainer: builder.query<Container, string>({
      query: (id) => ({
        url: `/containers/${id}`,
        method: "GET",
        params: buildParams(),
        headers: {}
      }),
      providesTags: (result, error, id) => [{type: "Containers", id}]
    }),
    getContainerImportTypes: builder.query<ImportType[], void>({
      query: () => ({
        url: `/containers/import-types`,
        method: "GET",
      }),
    }),
    createContainer: builder.mutation<Container, Partial<Container>>({
      query: (container) => ({
        url: '/containers',
        method: 'POST',
        body: container,
      }),
      invalidatesTags: [
        {type: 'Containers', id: 'LIST'},
        {type: "SiteContainers"},
        {type: "WellContainers"},
        {type: "ContainerClusterContainers"},
      ],
    }),
    updateContainer: builder.mutation<Container, Partial<Container>>({
      query: (container) => ({
        url: '/containers',
        method: 'PUT',
        body: container,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Containers', id}],
    }),
    patchUpdateContainer: builder.mutation<Container, { id: string, patch: ContainerPatchDTO }>({
      query: ({id, patch}) => ({
        url: `/containers/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Containers', id}],
    }),
    deleteContainer: builder.mutation<void, Partial<Container>>({
      query: (container) => ({
        url: '/containers',
        method: 'DELETE',
        body: container,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Containers', id}],
    }),
    updateContainerActive: builder.mutation<void, { containerId: string, regionId: string, active: boolean }>({
      query: ({containerId, active}) => ({
        url: `/containers/${containerId}/active`,
        method: 'PUT',
        body: {
          active: active
        },
      }),
      async onQueryStarted({containerId, regionId, active, ...patch}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled
          dispatch(
            objectChangeLogApi.util.invalidateTags([
              {type: 'ObjectObjectChangeLogs', id: containerId}
            ])
          )
        } catch (error) {
          console.debug(error)
        }
      },
      invalidatesTags: (_result, _error, {containerId}, _meta) => {
        return [
          {type: 'Containers', id: containerId},
          {type: "SiteContainers"},
          {type: "WellContainers"},
          {type: "ContainerClusterContainers"},
          {type: 'Containers', id: 'LIST'},
        ]
      },
    }),
    updateContainerDnu: builder.mutation<void, { containerId: string, regionId: string, dnu: boolean, clearDttm?: number, clearNotes?: string }>({
      query: ({containerId, dnu, clearDttm, clearNotes}) => ({
        url: `/containers/${containerId}/dnu`,
        method: 'PUT',
        body: {
          dnu: dnu,
          clearDttm: clearDttm,
          clearNotes: clearNotes,
        },
      }),
      async onQueryStarted({containerId, regionId, dnu, clearDttm, ...patch}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled
          for (const category of transportCategories) {
            dispatch(
              containerLevelApi.util.updateQueryData('getAllContainerStatuses', {
                regionId: regionId,
                category: category.code
              }, (draft) => {
                const statusIndex = draft.findIndex(s => s.containerId === containerId)
                if (statusIndex >= 0) {
                  const status = draft[statusIndex]
                  status.containerDoNotUse = dnu
                  status.containerClearDoNotUseScheduledDttm = clearDttm
                }
              })
            )
          }

          dispatch(
            objectChangeLogApi.util.invalidateTags([
              {type: 'ObjectObjectChangeLogs', id: containerId}
            ])
          )
        } catch (error) {
          console.debug(error)
        }
      },
      invalidatesTags: (_result, _error, {containerId}, _meta) => {
        return [
          {type: 'Containers', id: containerId},
          {type: "SiteContainers"},
          {type: "WellContainers"},
          {type: "ContainerClusterContainers"},
          {type: 'Containers', id: 'LIST'},
        ]
      },
    }),
    updateContainerDoNotDispatch: builder.mutation<void, {
      containerId: string,
      regionId: string,
      doNotDispatch: boolean,
      clearDttm?: number,
      clearNotes?: string
    }>({
      query: ({containerId, doNotDispatch, clearDttm, clearNotes}) => ({
        url: `/containers/${containerId}/do-not-dispatch`,
        method: 'PUT',
        body: {
          doNotDispatch: doNotDispatch,
          clearDttm: clearDttm,
          clearNotes: clearNotes,
        },
      }),
      async onQueryStarted({containerId, regionId, doNotDispatch, clearDttm, ...patch}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled
          for (const category of transportCategories) {
            dispatch(
              containerLevelApi.util.updateQueryData('getAllContainerStatuses', {
                regionId: regionId,
                category: category.code
              }, (draft) => {
                const statusIndex = draft.findIndex(s => s.containerId === containerId)
                if (statusIndex >= 0) {
                  const status = draft[statusIndex]
                  status.containerDoNotDispatch = doNotDispatch
                  status.containerClearDoNotDispatchScheduledDttm = clearDttm
                }
              })
            )
          }

          dispatch(
            objectChangeLogApi.util.invalidateTags([
              {type: 'ObjectObjectChangeLogs', id: containerId}
            ])
          )
        } catch (error) {
          console.debug(error)
        }
      },
      invalidatesTags: (_result, _error, {containerId}, _meta) => {
        return [
          {type: 'Containers', id: containerId},
          {type: "SiteContainers"},
          {type: "WellContainers"},
          {type: "ContainerClusterContainers"},
          {type: 'Containers', id: 'LIST'},
        ]
      },
    }),
    updateContainerDispatchIgnoreMaxNonPrimaryVolume: builder.mutation<void, {
      containerId: string,
      regionId: string,
      ignore: boolean
    }>({
      query: ({containerId, ignore}) => ({
        url: `/containers/${containerId}/dispatch-ignore-max-non-primary-volume`,
        method: 'PUT',
        body: {
          ignore: ignore
        },
      }),
      async onQueryStarted({containerId, regionId, ignore, ...patch}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled
          for (const category of transportCategories) {
            dispatch(
              containerLevelApi.util.updateQueryData('getAllContainerStatuses', {
                regionId: regionId,
                category: category.code
              }, (draft) => {
                const statusIndex = draft.findIndex(s => s.containerId === containerId)
                if (statusIndex >= 0) {
                  const status = draft[statusIndex]
                  status.containerDispatchIgnoreMaxNonPrimaryVolume = ignore
                }
              })
            )
          }

          dispatch(
            objectChangeLogApi.util.invalidateTags([
              {type: 'ObjectObjectChangeLogs', id: containerId}
            ])
          )
        } catch (error) {
          console.debug(error)
        }
      },
      invalidatesTags: (_result, _error, {containerId}, _meta) => {
        return [
          {type: 'Containers', id: containerId},
          {type: "SiteContainers"},
          {type: "WellContainers"},
          {type: "ContainerClusterContainers"},
          {type: 'Containers', id: 'LIST'}
        ]
      },
    }),
    updateContainerCargoSubtype: builder.mutation<void, {
      containerId: string,
      cargoSubtype: string,
      /** The page's region, whose freight types are used; the server falls back to the user's region. */
      regionId?: string
    }>({
      query: ({containerId, cargoSubtype, regionId}) => ({
        url: `/containers/${containerId}/cargo-subtype`,
        method: 'PUT',
        body: removeUndefined({
          cargoSubtype: cargoSubtype,
          regionId: regionId,
        }),
      }),
      async onQueryStarted({containerId}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled
          // switching oil/water moves the container between the water and oil status lists
          dispatch(containerLevelApi.util.invalidateTags([{type: 'ContainerStatuses'}]))
          dispatch(
            objectChangeLogApi.util.invalidateTags([
              {type: 'ObjectObjectChangeLogs', id: containerId}
            ])
          )
        } catch (error) {
          console.debug(error)
        }
      },
      invalidatesTags: (_result, _error, {containerId}, _meta) => {
        return [
          {type: 'Containers', id: containerId},
          {type: "SiteContainers"},
          {type: "WellContainers"},
          {type: "ContainerClusterContainers"},
          {type: 'Containers', id: 'LIST'}
        ]
      },
    }),
    importContainers: builder.mutation<void, { importType: ImportType, file: File }>({
      query: ({importType, file}) => {
        const formData = new FormData()
        formData.append("type", importType.code)
        formData.append("file", file)
        return ({
          url: `/containers/import`,
          method: 'POST',
          body: formData,
        })
      },
      invalidatesTags: (_result, _error, _arg, _meta) => {
        return [
          {type: 'Containers'},
          {type: "SiteContainers"},
          {type: "WellContainers"},
          {type: "ContainerClusterContainers"},
          {type: 'Containers', id: 'LIST'}
        ]
      },
    })
  })
});

const buildParams = (regionId?: string) => {
  const extrasParams = {
    withLevelData: false,
    withContainerData: true
  };

  if (regionId) {
    return {
      regionId: regionId,
      ...extrasParams
    };
  } else {
    return extrasParams;
  }
};

export const downloadContainerExport = (arg: ContainerExportOptions, api: BaseQueryApi): Promise<Response> => {

  const headers = buildStandardHeaders(api)
  headers["Accept"] = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  headers["Content-Type"] = 'application/json'

  const request: RequestInit = {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(arg),
  }

  const baseUrl = (api.extra as OtsAsyncThunkConfigExtra).baseUrl;
  const url = `${baseUrl}/container/export`;

  return window.fetch(url, request)
}

export const {
  useGetAllContainersQuery,
  useGetContainersBySiteQuery,
  useGetContainersByWellQuery,
  useGetContainersByContainerClusterQuery,
  useGetContainerQuery,
  useGetContainerImportTypesQuery,
  useCreateContainerMutation,
  useUpdateContainerMutation,
  usePatchUpdateContainerMutation,
  useDeleteContainerMutation,
  useUpdateContainerActiveMutation,
  useUpdateContainerDnuMutation,
  useUpdateContainerDoNotDispatchMutation,
  useUpdateContainerDispatchIgnoreMaxNonPrimaryVolumeMutation,
  useUpdateContainerCargoSubtypeMutation,
  useImportContainersMutation,
} = containerApi;
